import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { associationTypes, propertyTypes } from '@/config/constants';
import {
  useGetContractBySellerQuery,
  useUpdateContractSellerMutation,
} from '@/apis/extended/contractApi';
import { parseString } from '@/utils/helper';
import { CommonActions } from '../Common/CommonContract/CommonActions';
import { CommonPrevNextButtons } from '../Common/CommonContract/CommonPrevNextButtons';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';

const initialForm = {
  taxDate: '',
  taxAmount: '',
  escrowAgent: '',
  mortgageAmount: '',
  mortgageCompany: '',
  propertyType: '',
  associationType: '',
};

type ContractDetailSellerPropertyInfoProps = {
  uid: string;
  onPrev: () => void;
  onNext: () => void;
};

export const ContractDetailSellerPropertyInfo = ({
  uid,
  onPrev,
  onNext,
}: ContractDetailSellerPropertyInfoProps) => {
  const { data: contract } = useGetContractBySellerQuery(uid);
  const [updateContractSeller, { isLoading }] =
    useUpdateContractSellerMutation();

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    const sellerPropertyInfo = contract?.sellerPropertyInfo;
    if (!sellerPropertyInfo) return;
    setForm({
      taxDate: sellerPropertyInfo.taxDate || '',
      taxAmount: parseString(sellerPropertyInfo.taxAmount),
      mortgageAmount: parseString(sellerPropertyInfo.mortgageAmount),
      mortgageCompany: sellerPropertyInfo.mortgageCompany || '',
      escrowAgent: sellerPropertyInfo.escrowAgent || '',
      propertyType: sellerPropertyInfo.propertyType || '',
      associationType: sellerPropertyInfo.associationType || '',
    });
  }, [contract]);

  const isValid = useMemo(() => {
    return (
      form.taxDate &&
      form.taxAmount &&
      form.mortgageAmount &&
      form.mortgageCompany &&
      form.escrowAgent &&
      form.propertyType &&
      form.associationType
    );
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    await updateContractSeller({
      uid,
      sellerPropertyInfo: {
        ...form,
        taxAmount: parseFloat(form.taxAmount),
        mortgageAmount: parseFloat(form.mortgageAmount),
      },
    }).unwrap();
    onNext();
  };

  return (
    <Box>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Property Disclosure"
          action={
            <CommonActions
              isLoading={isLoading}
              isNextDisabled={!isValid}
              onPrev={onPrev}
              onNext={handleSubmit}
            />
          }
          avatar={<i className="fa-light fa-user" />}
        />
        <CardContent>
          <Box>
            <Typography variant="h5">
              The most recent property tax payment:
            </Typography>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Tax Date:"
              labelWidth={150}
            >
              <TextField
                placeholder="Tax Date"
                name="taxDate"
                type="date"
                value={form.taxDate}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Tax Amount:"
              labelWidth={150}
            >
              <CommonNumberInput
                placeholder="Tax Amount"
                name="taxAmount"
                value={form.taxAmount}
                onChange={handleInputChange}
              />
            </CommonFormControl>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h5">Current Mortgage Company:</Typography>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Mortgage Amount:"
              labelWidth={150}
            >
              <CommonNumberInput
                placeholder="Mortgage Amount"
                name="mortgageAmount"
                value={form.mortgageAmount}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Mortgage Company:"
              labelWidth={150}
            >
              <TextField
                placeholder="Mortgage Company"
                name="mortgageCompany"
                value={form.mortgageCompany}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Escrow Agent, or Attorney:"
              labelWidth={150}
            >
              <TextField
                placeholder="Escrow Agent"
                name="escrowAgent"
                value={form.escrowAgent}
                onChange={handleInputChange}
              />
            </CommonFormControl>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h5">Property Information:</Typography>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Property Type:"
              labelWidth={150}
            >
              <Select
                placeholder="Property Type"
                name="propertyType"
                value={form.propertyType}
                onChange={handleSelectChange}
              >
                {propertyTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </CommonFormControl>
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Association Type:"
              labelWidth={150}
            >
              <Select
                placeholder="Association Type"
                name="associationType"
                value={form.associationType}
                onChange={handleSelectChange}
              >
                {associationTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </CommonFormControl>
          </Box>
        </CardContent>
      </Card>
      <CommonPrevNextButtons
        sx={{ mt: 3 }}
        isLoading={isLoading}
        isNextDisabled={!isValid}
        onPrev={onPrev}
        onNext={handleSubmit}
      />
    </Box>
  );
};
